import { useEffect, useState } from 'react';
import { walkwithpath } from '../../theme/walkwithpath';

const themes: Record<string, () => Promise<any>> = {
  walkwithpath: () => import('../../theme/walkwithpath'),
  drc: () => import('../../theme/drc'),
};

export const useClientTheme = () => {
  const [theme, setTheme] = useState<any>(walkwithpath);
  const [isDefaultTheme, setIsDefaultTheme] = useState<boolean>(true);
  // Use a safe default if import.meta.env is not available (like in test environment)
  const requestedTheme =
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_APP_CLIENT_NAME) ||
    'walkwithpath';

  useEffect(() => {
    // Create an abort controller to cancel operations if component unmounts
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Define async function inside useEffect to avoid closure issues
    const loadThemeAsync = async () => {
      try {
        // Check if operation was aborted
        if (signal.aborted) return;

        if (themes[requestedTheme]) {
          const themeModule = await themes[requestedTheme]();
          // Check again if operation was aborted before updating state
          if (signal.aborted) return;

          const theme = themeModule.default || themeModule[requestedTheme];
          setTheme(theme);
          setIsDefaultTheme(theme?.clientId === 'walkwithpath');
        } else {
          throw new Error('Theme not found');
        }
      } catch (error) {
        // Only update state if not aborted
        if (!signal.aborted) {
          setTheme(walkwithpath);
          setIsDefaultTheme(true);
        }
      }
    };

    // Start loading theme
    loadThemeAsync();

    // Cleanup function that will be called when component unmounts
    return () => {
      abortController.abort();
    };
  }, [requestedTheme]); // Add requestedTheme as a dependency

  return { theme, isDefaultTheme };
};
